(() => {
  const ROOT_SELECTOR = '.twomatoes-webflow';
  const BUTTON_SELECTOR = `${ROOT_SELECTOR} .button[data-wf--button--variant]`;
  const SCALE_SELECTOR = `${ROOT_SELECTOR} [animation="scale"]`;
  const STORY_SELECTOR = `${ROOT_SELECTOR} .section-storytelling`;
  const ROTATING_SELECTOR = `${ROOT_SELECTOR} .section-rotating-images`;
  const EMOTIONAL_SELECTOR = `${ROOT_SELECTOR} .section-emotional`;
  const FAQ_SELECTOR = `${ROOT_SELECTOR} .section-faq`;
  const HOW_TO_USE_SELECTOR = `${ROOT_SELECTOR}[data-twomatoes-section="how-to-use"]`;
  const RESPONSIVE_QUERY = window.matchMedia('(max-width: 991px)');
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

  let activeScroller = null;
  let buttonMotionTimeline = null;
  let storytellingMedia = null;
  const scaleTweens = new Map();
  const rotatingAnimations = new Map();
  const emotionalAnimations = new Map();
  const howToUseSequences = new Map();

  const getScrollContainer = () => {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (!pageWrapper) return window;

    const styles = window.getComputedStyle(pageWrapper);
    const isScrollable = ['auto', 'scroll', 'overlay'].includes(styles.overflowY) && pageWrapper.scrollHeight > pageWrapper.clientHeight + 2;
    return isScrollable ? pageWrapper : window;
  };

  const initializeButtonHover = (root = document) => {
    root.querySelectorAll?.(BUTTON_SELECTOR).forEach((button) => {
      if (button.dataset.twomatoesButtonReady === 'true') return;

      const gradient = button.querySelector('.button_gradient');
      const glow = button.querySelector('.button_gradient-glow');

      if (gradient) window.gsap.set(gradient, { opacity: 0 });
      if (glow) window.gsap.set(glow, { opacity: 0 });

      button.dataset.twomatoesButtonReady = 'true';
      button.addEventListener('mouseenter', () => {
        if (gradient) window.gsap.to(gradient, { opacity: 1, duration: 0.3, ease: 'power1.in', overwrite: 'auto' });
        if (glow) window.gsap.to(glow, { opacity: 0.25, duration: 0.3, ease: 'power1.in', overwrite: 'auto' });
      });
      button.addEventListener('mouseleave', () => {
        if (gradient) window.gsap.to(gradient, { opacity: 0, duration: 0.3, ease: 'power1.in', overwrite: 'auto' });
        if (glow) window.gsap.to(glow, { opacity: 0, duration: 0.3, ease: 'power1.in', overwrite: 'auto' });
      });
      button.addEventListener('focusin', () => {
        if (gradient) window.gsap.to(gradient, { opacity: 1, duration: 0.3, ease: 'power1.in', overwrite: 'auto' });
        if (glow) window.gsap.to(glow, { opacity: 0.25, duration: 0.3, ease: 'power1.in', overwrite: 'auto' });
      });
      button.addEventListener('focusout', () => {
        if (gradient) window.gsap.to(gradient, { opacity: 0, duration: 0.3, ease: 'power1.in', overwrite: 'auto' });
        if (glow) window.gsap.to(glow, { opacity: 0, duration: 0.3, ease: 'power1.in', overwrite: 'auto' });
      });
    });
  };

  const initializeButtonMotion = () => {
    if (buttonMotionTimeline) buttonMotionTimeline.kill();
    buttonMotionTimeline = null;

    const ball1 = window.gsap.utils.toArray(`${BUTTON_SELECTOR} .button_gradient-ball.ball-1`);
    const ball2 = window.gsap.utils.toArray(`${BUTTON_SELECTOR} .button_gradient-ball.ball-2`);

    window.gsap.set(ball1, { xPercent: 0, yPercent: 0, scale: 1, force3D: true });
    window.gsap.set(ball2, { xPercent: 0, yPercent: 0, scale: 1, force3D: true });

    if (REDUCED_MOTION_QUERY.matches || (!ball1.length && !ball2.length)) return;

    buttonMotionTimeline = window.gsap.timeline({ repeat: -1 });

    if (ball1.length) {
      buttonMotionTimeline.to(ball1, { xPercent: 100, duration: 3, ease: 'none' }, 0);
      buttonMotionTimeline.to(ball1, { yPercent: -100, scale: 1.5, duration: 3, ease: 'none' }, 3);
      buttonMotionTimeline.to(ball1, { xPercent: 0, duration: 3, ease: 'none' }, 6);
      buttonMotionTimeline.to(ball1, { yPercent: 0, scale: 1, duration: 3, ease: 'none' }, 9);
    }

    if (ball2.length) {
      buttonMotionTimeline.to(ball2, { xPercent: -100, duration: 4, ease: 'none' }, 0);
      buttonMotionTimeline.to(ball2, { yPercent: -100, duration: 4, ease: 'none' }, 4);
      buttonMotionTimeline.to(ball2, { yPercent: 0, duration: 4, ease: 'none' }, 8);
      buttonMotionTimeline.to(ball2, { xPercent: 0, duration: 4, ease: 'none' }, 12);
    }
  };


  // Play the How it works clips in order, never simultaneously. Each section
  // owns its playback state and is safe to replace in Shopify's theme editor.
  const initializeHowToUseSequence = (section) => {
    if (howToUseSequences.has(section)) return;

    const videos = Array.from(section.querySelectorAll('video.twomatoes-how-to-use-video'));
    if (!videos.length) return;

    let currentIndex = 0;
    let inView = false;
    let destroyed = false;
    let playRequest = 0;
    const failedVideos = new Set();

    videos.forEach((video) => {
      video.autoplay = false;
      video.loop = false;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.removeAttribute('autoplay');
      video.removeAttribute('loop');
      video.pause();
    });

    const pauseAll = () => {
      playRequest += 1;
      videos.forEach((video) => video.pause());
    };

    const moveToNext = () => {
      if (destroyed || !inView || document.hidden) return;
      if (failedVideos.size === videos.length) {
        pauseAll();
        return;
      }

      for (let offset = 1; offset <= videos.length; offset += 1) {
        const nextIndex = (currentIndex + offset) % videos.length;
        if (failedVideos.has(videos[nextIndex])) continue;
        currentIndex = nextIndex;
        startCurrent(true);
        return;
      }
    };

    const startCurrent = (restart = false) => {
      if (destroyed || !inView || document.hidden) return;

      const video = videos[currentIndex];
      if (!video || failedVideos.has(video)) {
        moveToNext();
        return;
      }

      videos.forEach((other) => {
        if (other !== video) other.pause();
      });

      if (restart) {
        try { video.currentTime = 0; } catch (_error) {}
      }

      const requestId = ++playRequest;
      let attempt;
      try {
        attempt = video.play();
      } catch (_error) {
        failedVideos.add(video);
        moveToNext();
        return;
      }

      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch((error) => {
          if (destroyed || !inView || document.hidden || requestId !== playRequest) return;

          // Autoplay may be blocked by a browser preference. Do not spin
          // through all clips if the user has disabled autoplay.
          if (error?.name === 'NotAllowedError') return;

          // A missing/unsupported video should not block the whole sequence.
          failedVideos.add(video);
          moveToNext();
        });
      }
    };

    videos.forEach((video, index) => {
      const onEnd = () => {
        if (index === currentIndex) moveToNext();
      };
      const onError = () => {
        failedVideos.add(video);
        if (index === currentIndex) moveToNext();
      };
      video.addEventListener('ended', onEnd);
      video.addEventListener('error', onError);
      video.twomatoesSequenceListeners = { onEnd, onError };
    });

    const handleVisibility = () => {
      if (destroyed) return;
      if (document.hidden || !inView) pauseAll();
      else startCurrent();
    };

    document.addEventListener('visibilitychange', handleVisibility);

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(([entry]) => {
          const nowInView = entry.isIntersecting && entry.intersectionRatio > 0.1;
          if (inView === nowInView) return;
          inView = nowInView;
          handleVisibility();
        }, { threshold: [0, 0.1, 0.25] })
      : null;

    if (observer) observer.observe(section);
    else {
      inView = true;
      handleVisibility();
    }

    howToUseSequences.set(section, () => {
      destroyed = true;
      observer?.disconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
      pauseAll();

      videos.forEach((video) => {
        const listeners = video.twomatoesSequenceListeners;
        if (listeners) {
          video.removeEventListener('ended', listeners.onEnd);
          video.removeEventListener('error', listeners.onError);
          delete video.twomatoesSequenceListeners;
        }
      });
    });
  };

  const registerHowToUseSequences = (root = document) => {
    if (root.matches?.(HOW_TO_USE_SELECTOR)) initializeHowToUseSequence(root);
    root.querySelectorAll?.(HOW_TO_USE_SELECTOR).forEach(initializeHowToUseSequence);
  };

  const removeDisconnectedHowToUseSequences = () => {
    howToUseSequences.forEach((cleanup, section) => {
      if (section.isConnected) return;
      cleanup();
      howToUseSequences.delete(section);
    });
  };

  const initializeStorytelling = () => {
    storytellingMedia?.revert();
    storytellingMedia = window.gsap.matchMedia();

    storytellingMedia.add('(min-width: 992px)', () => {
      const timelines = [];

      document.querySelectorAll(STORY_SELECTOR).forEach((section) => {
        const imageWraps = window.gsap.utils.toArray('.about-values_img-wrap', section);
        const contentBlocks = window.gsap.utils.toArray('.about-values_block-content', section);
        const itemCount = Math.min(imageWraps.length, contentBlocks.length);

        if (!itemCount) return;

        const activeImages = imageWraps.slice(0, itemCount);
        const activeContent = contentBlocks.slice(0, itemCount);

        window.gsap.set(activeImages[0], { opacity: 1, scale: 1, transformOrigin: '50% 50%', force3D: true });
        if (itemCount > 1) window.gsap.set(activeImages.slice(1), { opacity: 0, scale: 1.1, transformOrigin: '50% 50%', force3D: true });
        window.gsap.set(activeContent[0], { opacity: 1 });
        if (itemCount > 1) window.gsap.set(activeContent.slice(1), { opacity: 0.2 });

        if (REDUCED_MOTION_QUERY.matches || itemCount === 1) {
          window.gsap.set(activeContent, { opacity: 1 });
          return;
        }

        const scrollTrigger = {
          trigger: section,
          start: 'clamp(top top)',
          end: 'clamp(bottom 120%)',
          scrub: 0.8,
          invalidateOnRefresh: true
        };

        if (activeScroller !== window) scrollTrigger.scroller = activeScroller;

        const timeline = window.gsap.timeline({ scrollTrigger });

        for (let index = 1; index < itemCount; index += 1) {
          const position = 0.5 + ((index - 1) * 0.7);
          timeline.to(activeImages[index], { opacity: 1, scale: 1, duration: 0.05, ease: 'power1.out' }, position);
          timeline.to(activeContent[index - 1], { opacity: 0.2, duration: 0.2, ease: 'none' }, position);
          timeline.to(activeContent[index], { opacity: 1, duration: 0.2, ease: 'none' }, position);
        }

        timelines.push(timeline);
      });

      return () => timelines.forEach((timeline) => timeline.kill());
    });

    storytellingMedia.add('(max-width: 991px)', () => {
      document.querySelectorAll(`${STORY_SELECTOR} .about-values_block-content`).forEach((element) => window.gsap.set(element, { clearProps: 'opacity,transform' }));
    });
  };


  const splitHeadingWords = (heading) => {
    if (heading.dataset.twomatoesWordsReady === 'true') return window.gsap.utils.toArray('.home-header_heading-word', heading);

    const text = heading.textContent.trim();
    heading.dataset.twomatoesOriginalText = text;
    heading.setAttribute('aria-label', text);
    heading.textContent = '';

    text.split(/\s+/).forEach((word, index, words) => {
      const span = document.createElement('span');
      span.className = 'home-header_heading-word';
      span.setAttribute('aria-hidden', 'true');
      span.textContent = word;
      heading.appendChild(span);
      if (index < words.length - 1) heading.appendChild(document.createTextNode(' '));
    });

    heading.dataset.twomatoesWordsReady = 'true';
    return window.gsap.utils.toArray('.home-header_heading-word', heading);
  };

  const initializeRotatingSection = (section) => {
    if (rotatingAnimations.has(section)) return;

    const heading1 = section.querySelector('.home-header_heading._1');
    const heading2 = section.querySelector('.home-header_heading._2');
    const heading3 = section.querySelector('.home-header_heading._3');
    const imageRing = section.querySelector('.home-header_imgs-wrap');
    const imageWraps = window.gsap.utils.toArray('.home-header_img-wrap', section);
    const images = window.gsap.utils.toArray('.home-header_img', section);
    const timelines = [];

    if (!heading1 || !heading2 || !heading3 || !imageRing || !imageWraps.length || !images.length) return;

    const words1 = splitHeadingWords(heading1);
    const words2 = splitHeadingWords(heading2);
    const words3 = splitHeadingWords(heading3);

    window.gsap.set([heading2, heading3], { display: 'block' });
    window.gsap.set([words1, words2, words3], { opacity: 0, y: '2rem', force3D: true });
    window.gsap.set(imageRing, { rotation: 0, transformOrigin: '50% 50%', force3D: true });
    window.gsap.set(imageWraps, { rotation: 0, transformOrigin: '50% 50%', force3D: true });
    window.gsap.set(images, { rotationY: 0, transformPerspective: 600, force3D: true });

    if (REDUCED_MOTION_QUERY.matches) {
      window.gsap.set(words1, { opacity: 1, y: 0 });
      window.gsap.set([heading2, heading3], { display: 'none' });
      rotatingAnimations.set(section, () => {});
      return;
    }

    const headingTimeline = window.gsap.timeline({ repeat: -1, delay: 0.25 });
    headingTimeline.fromTo(words1, { opacity: 0, y: '2rem' }, { opacity: 1, y: 0, duration: 1, stagger: 0.1, ease: 'back.inOut' }, 0);
    headingTimeline.to(words1, { opacity: 0, y: '-2rem', duration: 1, stagger: 0.1, ease: 'back.inOut' }, 8);
    headingTimeline.fromTo(words2, { opacity: 0, y: '2rem' }, { opacity: 1, y: 0, duration: 1, stagger: 0.1, ease: 'back.inOut' }, 9);
    headingTimeline.to(words2, { opacity: 0, y: '-2rem', duration: 1, stagger: 0.1, ease: 'back.inOut' }, 16);
    headingTimeline.fromTo(words3, { opacity: 0, y: '2rem' }, { opacity: 1, y: 0, duration: 1, stagger: 0.1, ease: 'back.inOut' }, 17);
    headingTimeline.to(words3, { opacity: 0, y: '-2rem', duration: 1, stagger: 0.1, ease: 'back.inOut' }, 23);
    timelines.push(headingTimeline);

    const orbitTimeline = window.gsap.timeline({ repeat: -1 });
    orbitTimeline.to(imageRing, { rotation: 360, duration: 460, ease: 'none' }, 0);
    orbitTimeline.to(imageWraps, { rotation: -360, duration: 460, ease: 'none' }, 0);
    timelines.push(orbitTimeline);

    const flipTimeline = window.gsap.timeline({ repeat: -1, delay: 3 });
    flipTimeline.fromTo(images, { rotationY: 0, transformPerspective: 600 }, { rotationY: 180, transformPerspective: 600, duration: 2, stagger: 0.5, ease: 'power1.in' }, 0);
    flipTimeline.to(images, { rotationY: 360, transformPerspective: 600, duration: 2, stagger: 0.5, ease: 'power1.inOut' }, 16);
    timelines.push(flipTimeline);

    rotatingAnimations.set(section, () => timelines.forEach((timeline) => timeline.kill()));
  };

  const registerRotatingSections = (root = document) => {
    if (root.matches?.(ROTATING_SELECTOR)) initializeRotatingSection(root);
    root.querySelectorAll?.(ROTATING_SELECTOR).forEach(initializeRotatingSection);
  };

  const removeDisconnectedRotatingAnimations = () => {
    rotatingAnimations.forEach((cleanup, section) => {
      if (section.isConnected) return;
      cleanup();
      rotatingAnimations.delete(section);
    });
  };


  const initializeEmotionalSection = (section) => {
    if (emotionalAnimations.has(section)) return;

    const wrapper = section.querySelector('.home-about_wrapper');
    const component = section.querySelector('.home-about_component');
    const media = section.querySelector('.home-about_img');
    const imageText = section.querySelector('.home-about_img-text');
    const headings = window.gsap.utils.toArray('.home-about_heading', section);
    const scrollNote = section.querySelector('.scroll-note_text');
    const animations = [];

    if (!wrapper || !component || !media || !imageText || headings.length < 2) return;

    if (REDUCED_MOTION_QUERY.matches) {
      window.gsap.set(component, { width: '100vw', height: '85vh' });
      window.gsap.set(media, { scale: 1, transformOrigin: '50% 50%' });
      window.gsap.set(imageText, { opacity: 1 });
      window.gsap.set(headings, { opacity: 0 });
      if (media instanceof HTMLVideoElement) media.pause();
      if (scrollNote) window.gsap.set(scrollNote, { opacity: 1, y: 0 });
      emotionalAnimations.set(section, () => {});
      return;
    }

    window.gsap.set(component, { width: '10vw', height: '25vh', force3D: true });
    window.gsap.set(media, { scale: 1.4, transformOrigin: '50% 50%', force3D: true });
    window.gsap.set(imageText, { opacity: 0 });
    window.gsap.set(headings, { opacity: 1 });

    const scrollTrigger = {
      trigger: wrapper,
      start: 'clamp(top top)',
      end: 'clamp(bottom bottom)',
      scrub: 0.8,
      invalidateOnRefresh: true
    };

    if (activeScroller !== window) scrollTrigger.scroller = activeScroller;

    const timeline = window.gsap.timeline({ scrollTrigger });
    timeline.to(component, { width: '100vw', height: '85vh', duration: 0.15, ease: 'power1.inOut' }, 0);
    timeline.to(media, { scale: 1, duration: 0.15, ease: 'none' }, 0);
    timeline.to(imageText, { opacity: 1, duration: 0.1, ease: 'power1.inOut' }, 0.05);
    timeline.to(headings, { opacity: 0, duration: 0.1, ease: 'power1.inOut' }, 0.05);
    animations.push(timeline);

    if (scrollNote) {
      window.gsap.set(scrollNote, { opacity: 0, y: '0.5rem' });

      const noteScrollTrigger = {
        trigger: scrollNote,
        start: 'clamp(top 70%)',
        end: 'clamp(bottom top)',
        toggleActions: 'play none none none',
        invalidateOnRefresh: true
      };

      if (activeScroller !== window) noteScrollTrigger.scroller = activeScroller;

      const noteTween = window.gsap.to(scrollNote, {
        opacity: 1,
        y: 0,
        duration: 0.33,
        ease: 'power1.inOut',
        scrollTrigger: noteScrollTrigger
      });
      animations.push(noteTween);
    }

    emotionalAnimations.set(section, () => {
      animations.forEach((animation) => {
        animation.scrollTrigger?.kill();
        animation.kill();
      });
    });
  };

  const registerEmotionalSections = (root = document) => {
    if (root.matches?.(EMOTIONAL_SELECTOR)) initializeEmotionalSection(root);
    root.querySelectorAll?.(EMOTIONAL_SELECTOR).forEach(initializeEmotionalSection);
  };

  const removeDisconnectedEmotionalAnimations = () => {
    emotionalAnimations.forEach((cleanup, section) => {
      if (section.isConnected) return;
      cleanup();
      emotionalAnimations.delete(section);
    });
  };


  const refreshScrollTriggers = () => {
    if (typeof window.ScrollTrigger === 'undefined') return;
    requestAnimationFrame(() => window.ScrollTrigger.refresh());
  };

  const setFaqState = (item, open, animate = true) => {
    const button = item.querySelector('[data-twomatoes-faq-button]');
    const answer = item.querySelector('[data-twomatoes-faq-answer]');
    const answerInner = answer?.querySelector('.faq_answer-inner');

    if (!button || !answer || !answerInner) return;

    button.setAttribute('aria-expanded', String(open));
    answer.setAttribute('aria-hidden', String(!open));
    item.classList.toggle('is-open', open);

    window.gsap.killTweensOf([answer, answerInner]);

    if (!animate || REDUCED_MOTION_QUERY.matches) {
      window.gsap.set(answer, { height: open ? 'auto' : 0 });
      window.gsap.set(answerInner, { clearProps: 'transform' });
      window.gsap.set(answerInner, { opacity: open ? 1 : 0 });
      refreshScrollTriggers();
      return;
    }

    if (open) {
      window.gsap.to(answer, {
        height: 'auto',
        duration: 0.4,
        ease: 'power2.inOut',
        overwrite: 'auto',
        onComplete: () => {
          window.gsap.set(answer, { height: 'auto' });
          refreshScrollTriggers();
        }
      });

      window.gsap.set(answerInner, { clearProps: 'transform' });
      window.gsap.to(answerInner, {
        opacity: 1,
        duration: 0.3,
        ease: 'power1.out',
        overwrite: 'auto'
      });
      return;
    }

    const currentHeight = answer.getBoundingClientRect().height;
    window.gsap.set(answer, { height: currentHeight });

    window.gsap.to(answer, {
      height: 0,
      duration: 0.4,
      ease: 'power2.inOut',
      overwrite: 'auto',
      onComplete: refreshScrollTriggers
    });

    window.gsap.set(answerInner, { clearProps: 'transform' });
    window.gsap.to(answerInner, {
      opacity: 0,
      duration: 0.25,
      ease: 'power1.in',
      overwrite: 'auto'
    });
  };

  const initializeFaqItem = (item) => {
    if (item.dataset.twomatoesFaqReady === 'true') return;

    const button = item.querySelector('[data-twomatoes-faq-button]');
    if (!button) return;

    item.dataset.twomatoesFaqReady = 'true';
    setFaqState(item, button.getAttribute('aria-expanded') === 'true', false);

    button.addEventListener('click', () => {
      const willOpen = button.getAttribute('aria-expanded') !== 'true';
      setFaqState(item, willOpen, true);
    });
  };

  const registerFaqSections = (root = document) => {
    if (root.matches?.(FAQ_SELECTOR)) root.querySelectorAll('[data-twomatoes-faq-item]').forEach(initializeFaqItem);
    root.querySelectorAll?.(`${FAQ_SELECTOR} [data-twomatoes-faq-item]`).forEach(initializeFaqItem);
  };

  const registerScaleElement = (element) => {
    if (scaleTweens.has(element)) return;

    window.gsap.set(element, { scale: 1, transformOrigin: '50% 50%', force3D: true });
    if (REDUCED_MOTION_QUERY.matches) return;

    const scrollTrigger = {
      trigger: element,
      start: 'clamp(top bottom)',
      end: 'clamp(bottom top)',
      scrub: 0.8,
      invalidateOnRefresh: true
    };

    if (activeScroller !== window) scrollTrigger.scroller = activeScroller;

    const tween = window.gsap.to(element, {
      scale: 1.15,
      duration: 0.8,
      ease: 'power1.in',
      scrollTrigger
    });

    scaleTweens.set(element, tween);
  };

  const registerScaleElements = (root = document) => {
    if (root.matches?.(SCALE_SELECTOR)) registerScaleElement(root);
    root.querySelectorAll?.(SCALE_SELECTOR).forEach(registerScaleElement);
  };

  const removeDisconnectedScaleTweens = () => {
    scaleTweens.forEach((tween, element) => {
      if (element.isConnected) return;
      tween.scrollTrigger?.kill();
      tween.kill();
      scaleTweens.delete(element);
    });
  };

  const clearScaleTweens = () => {
    scaleTweens.forEach((tween) => {
      tween.scrollTrigger?.kill();
      tween.kill();
    });
    scaleTweens.clear();
  };

  const clearEmotionalAnimations = () => {
    emotionalAnimations.forEach((cleanup) => cleanup());
    emotionalAnimations.clear();
  };

  const rebuildScrollerAnimations = () => {
    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;

    storytellingMedia?.revert();
    storytellingMedia = null;
    clearScaleTweens();
    clearEmotionalAnimations();

    activeScroller = getScrollContainer();

    registerScaleElements();
    initializeStorytelling();
    registerEmotionalSections();

    requestAnimationFrame(() => {
      window.ScrollTrigger.refresh();
      window.ScrollTrigger.update();
    });
  };

  const handleResponsiveScrollerChange = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(rebuildScrollerAnimations);
    });
  };

  const initialize = () => {
    registerHowToUseSequences();
    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;

    window.gsap.registerPlugin(window.ScrollTrigger);

    // Horizon uses a custom scroll container. ScrollTrigger's default automatic
    // refresh on window.load can reset that container if the visitor has already
    // started scrolling, so initialize after DOMContentLoaded and exclude load
    // from automatic refresh events.
    window.ScrollTrigger.config({ autoRefreshEvents: 'visibilitychange,resize' });
    activeScroller = getScrollContainer();

    initializeButtonHover();
    initializeButtonMotion();
    registerScaleElements();
    initializeStorytelling();
    registerRotatingSections();
    registerEmotionalSections();
    registerFaqSections();

    // Creating the triggers calculates their positions. Do not run a load-time
    // global refresh because it can reset Horizon's .page-wrapper scrollTop.
    window.ScrollTrigger.update();
  };

  const initializeEditorSection = (event) => {
    removeDisconnectedHowToUseSequences();
    registerHowToUseSequences(event.target);
    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;

    initializeButtonHover(event.target);
    initializeButtonMotion();
    registerScaleElements(event.target);
    removeDisconnectedScaleTweens();
    initializeStorytelling();
    registerRotatingSections(event.target);
    removeDisconnectedRotatingAnimations();
    registerEmotionalSections(event.target);
    removeDisconnectedEmotionalAnimations();
    registerFaqSections(event.target);

    // Shopify editor only: recalculate after a section is inserted or reordered.
    requestAnimationFrame(() => window.ScrollTrigger.refresh());
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', initializeEditorSection);
    document.addEventListener('shopify:section:reorder', () => requestAnimationFrame(() => window.ScrollTrigger.refresh()));
    document.addEventListener('shopify:section:unload', (event) => {
      howToUseSequences.forEach((cleanup, section) => {
        if (event.target === section || event.target.contains(section)) {
          cleanup();
          howToUseSequences.delete(section);
        }
      });
      removeDisconnectedHowToUseSequences();
      removeDisconnectedScaleTweens();
      removeDisconnectedRotatingAnimations();
      removeDisconnectedEmotionalAnimations();
    });
  }

  RESPONSIVE_QUERY.addEventListener('change', handleResponsiveScrollerChange);
  REDUCED_MOTION_QUERY.addEventListener('change', () => window.location.reload());
})();


/* ==========================================================================
   Twomatoes Instagram feed — centered infinite carousel (v7)
   ========================================================================== */
(() => {
  'use strict';

  if (window.__twomatoesInstagramFeedSharedV7) return;
  window.__twomatoesInstagramFeedSharedV7 = true;

  const SECTION_SELECTOR = '[data-twomatoes-section="instagram-feed"]';
  const active = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const validImage = (url) => {
    if (typeof url !== 'string') return false;
    try { return new URL(url).protocol === 'https:'; } catch { return false; }
  };

  const setMessage = (message, content, hidden = false) => {
    if (!message) return;
    message.textContent = content;
    message.classList.toggle('twomatoes-instagram__message--visually-hidden', hidden);
  };

  const createPost = (post, index, duplicate = false) => {
    const card = document.createElement('div');
    card.className = 'twomatoes-instagram__post';
    if (duplicate) {
      card.setAttribute('aria-hidden', 'true');
    } else {
      card.setAttribute('role', 'listitem');
    }

    const image = document.createElement('img');
    image.src = post.imageUrl;
    image.alt = duplicate ? '' : (typeof post.alt === 'string' ? post.alt.slice(0, 180) : 'Instagram post from @twomatoescom');
    image.loading = index < 5 ? 'eager' : 'lazy';
    image.decoding = 'async';
    image.draggable = false;
    image.referrerPolicy = 'no-referrer';
    image.addEventListener('error', () => card.classList.add('twomatoes-instagram__post--broken'), { once: true });
    card.appendChild(image);
    return card;
  };

  const setupCarousel = (section, viewport, track, state, postCount) => {
    const previous = section.querySelector('[data-instagram-prev]');
    const next = section.querySelector('[data-instagram-next]');
    const items = Array.from(track.children);
    if (!items.length) return;

    let motionFrame = 0;
    let resizeFrame = 0;
    let idleTimer = 0;
    let latestIndex = 0;
    let targetIndex = postCount;
    let previousWidth = viewport.clientWidth;
    let drag = null;
    let disposed = false;

    // Use Touch Events on every touch device. Unlike Safari's native inertia,
    // this gives the same one-post-per-gesture result on iOS and Android.
    // Mouse and pen continue using Pointer Events below.
    let touchGesture = null;

    const mod = (n, size) => ((n % size) + size) % size;
    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
    const isScrollable = () => viewport.scrollWidth > viewport.clientWidth + 2;
    const centerOffset = (index) => items[index].offsetLeft + items[index].offsetWidth / 2 - viewport.clientWidth / 2;
    const nearestIndex = (scrollLeft = viewport.scrollLeft) => {
      const width = items[0].offsetWidth;
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const center = scrollLeft + viewport.clientWidth / 2;
      return clamp(Math.round((center - items[0].offsetLeft - width / 2) / (width + gap)), 0, items.length - 1);
    };
    const stopMotion = () => {
      if (motionFrame) window.cancelAnimationFrame(motionFrame);
      motionFrame = 0;
    };
    const stopIdle = () => window.clearTimeout(idleTimer);
    const updateButtons = () => {
      if (previous) previous.disabled = !isScrollable();
      if (next) next.disabled = !isScrollable();
    };

    // There are three identical post sets. If we approach the edges, shift
    // one complete set (identical image and fractional alignment, so invisible).
    const settle = () => {
      if (disposed || drag || touchGesture || motionFrame) return;
      let index = nearestIndex();
      latestIndex = mod(index, postCount);
      if (index < postCount - 2 || index > postCount * 2 + 1) {
        index = postCount + latestIndex;
        viewport.scrollLeft = centerOffset(index);
      }
      targetIndex = index;
    };

    // Own the release animation instead of switching CSS scroll snapping on:
    // that switch previously caused the browser to jump instantly on release.
    const animateTo = (index, animated = true) => {
      stopMotion();
      stopIdle();
      const destination = centerOffset(index);
      targetIndex = index;
      latestIndex = mod(index, postCount);
      const start = viewport.scrollLeft;
      const difference = destination - start;
      if (!animated || reducedMotion.matches || Math.abs(difference) < 1) {
        viewport.scrollLeft = destination;
        settle();
        return;
      }
      const cardStep = items[0].offsetWidth + (parseFloat(window.getComputedStyle(track).columnGap) || 0);
      const duration = clamp(270 + Math.abs(difference) / Math.max(cardStep, 1) * 90, 270, 480);
      let beganAt = null;
      const tick = (timestamp) => {
        if (disposed) return;
        if (beganAt === null) beganAt = timestamp;
        const progress = clamp((timestamp - beganAt) / duration, 0, 1);
        // Ease out cubic: the card moves immediately, then decelerates gently.
        const eased = 1 - Math.pow(1 - progress, 3);
        viewport.scrollLeft = start + difference * eased;
        if (progress < 1) {
          motionFrame = window.requestAnimationFrame(tick);
        } else {
          motionFrame = 0;
          viewport.scrollLeft = destination;
          settle();
        }
      };
      motionFrame = window.requestAnimationFrame(tick);
    };

    const onScroll = () => {
      if (disposed || drag || touchGesture || motionFrame) return;
      stopIdle();
      if (touchGesture) return; // Do not snap while a finger is still on the strip.
      // Trackpads can scroll freely. When scrolling stops, ease to the closest post.
      idleTimer = window.setTimeout(() => {
        idleTimer = 0;
        if (!drag && !motionFrame) animateTo(nearestIndex());
      }, 180);
    };

    const navigate = (direction) => {
      if (!isScrollable() || drag || touchGesture) return;
      // Rebase before exhausting the three sets, retaining the exact picture.
      // This also works when multiple arrows are pressed during animation.
      if ((direction > 0 && targetIndex >= items.length - 3) || (direction < 0 && targetIndex <= 2)) {
        const shifted = targetIndex + (direction > 0 ? -postCount : postCount);
        stopMotion();
        viewport.scrollLeft += centerOffset(shifted) - centerOffset(targetIndex);
        targetIndex = shifted;
      }
      animateTo(clamp(targetIndex + direction, 0, items.length - 1));
    };

    const onKeyDown = (event) => {
      if (event.target !== viewport) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      navigate(event.key === 'ArrowRight' ? 1 : -1);
    };
    const onPrevious = () => navigate(-1);
    const onNext = () => navigate(1);

    // A gesture moves at most ONE post, regardless of drag distance or speed.
    // Clamp visual drag distance to a single step, then ease to the next center.
    const cardStep = () => items[0].offsetWidth + (parseFloat(window.getComputedStyle(track).columnGap) || 0);
    const gestureThreshold = () => Math.max(17, Math.min(48, cardStep() * 0.13));
    const dragOffset = (dx) => clamp(dx, -cardStep() * 0.92, cardStep() * 0.92);
    const gestureDirection = (dx, elapsed) => {
      const isQuickFlick = Math.abs(dx) >= 12 && elapsed > 0 && Math.abs(dx) / elapsed >= 0.32;
      if (Math.abs(dx) < gestureThreshold() && !isQuickFlick) return 0;
      return dx < 0 ? 1 : -1;
    };

    const onPointerDown = (event) => {
      // Pointer Events can be cancelled by iOS during native touch handling.
      // All finger gestures instead use the Touch Events handlers below.
      if (event.pointerType === 'touch') return;
      if (drag || touchGesture || !isScrollable() || (event.pointerType === 'mouse' && event.button !== 0) || event.isPrimary === false) return;
      stopMotion();
      stopIdle();
      drag = {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        startScroll: viewport.scrollLeft,
        startIndex: nearestIndex(),
        startedAt: performance.now(),
        lastDx: 0,
        moved: false,
        vertical: false
      };
      targetIndex = drag.startIndex;
    };
    const onPointerMove = (event) => {
      if (!drag || event.pointerId !== drag.pointerId || drag.vertical) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved) {
        if (Math.abs(dx) < 7 && Math.abs(dy) < 7) return;
        if (Math.abs(dy) > Math.abs(dx) * 1.1) {
          drag.vertical = true;
          return;
        }
        if (Math.abs(dx) < 7) return;
        drag.moved = true;
        viewport.classList.add('is-dragging');
        try { viewport.setPointerCapture(event.pointerId); } catch { /* optional */ }
      }
      if (event.cancelable) event.preventDefault();
      drag.lastDx = dx;
      viewport.scrollLeft = drag.startScroll - dragOffset(dx);
    };
    const finishPointer = (event, cancelled = false) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const gesture = drag;
      drag = null;
      viewport.classList.remove('is-dragging');
      if (viewport.hasPointerCapture?.(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      if (gesture.vertical || !gesture.moved) {
        onScroll();
        return;
      }
      const elapsed = performance.now() - gesture.startedAt;
      const direction = cancelled ? 0 : gestureDirection(gesture.lastDx, elapsed);
      animateTo(gesture.startIndex + direction, true);
    };
    const onPointerUp = (event) => finishPointer(event);
    const onPointerCancel = (event) => finishPointer(event, true);
    const onDragStart = (event) => event.preventDefault();

    // Non-passive touchmove is important on iPhone Safari. Once the gesture
    // is horizontal, we prevent native momentum and control scrollLeft ourselves.
    // Vertical gestures are NOT prevented, so normal page scrolling still works.
    const getTouch = (touches, id) => Array.from(touches).find((touch) => touch.identifier === id);
    const onTouchStart = (event) => {
      if (event.touches.length !== 1 || drag || !isScrollable()) return;
      const touch = event.touches[0];
      stopMotion();
      stopIdle();
      touchGesture = {
        identifier: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        startScroll: viewport.scrollLeft,
        startIndex: nearestIndex(),
        startedAt: performance.now(),
        lastDx: 0,
        axis: null
      };
      targetIndex = touchGesture.startIndex;
    };
    const onTouchMove = (event) => {
      if (!touchGesture || event.touches.length !== 1) return;
      const touch = getTouch(event.touches, touchGesture.identifier);
      if (!touch) return;
      const dx = touch.clientX - touchGesture.startX;
      const dy = touch.clientY - touchGesture.startY;
      if (!touchGesture.axis) {
        if (Math.abs(dx) < 7 && Math.abs(dy) < 7) return;
        if (Math.abs(dy) > Math.abs(dx) * 1.1) {
          touchGesture.axis = 'vertical';
          return;
        }
        if (Math.abs(dx) <= Math.abs(dy)) return;
        touchGesture.axis = 'horizontal';
        viewport.classList.add('is-dragging');
      }
      if (touchGesture.axis !== 'horizontal') return;
      if (event.cancelable) event.preventDefault();
      touchGesture.lastDx = dx;
      viewport.scrollLeft = touchGesture.startScroll - dragOffset(dx);
    };
    const finishTouch = (event, cancelled = false) => {
      const gesture = touchGesture;
      if (!gesture || !getTouch(event.changedTouches, gesture.identifier)) return;
      touchGesture = null;
      viewport.classList.remove('is-dragging');
      if (gesture.axis !== 'horizontal') {
        onScroll();
        return;
      }
      const elapsed = performance.now() - gesture.startedAt;
      const direction = cancelled ? 0 : gestureDirection(gesture.lastDx, elapsed);
      animateTo(gesture.startIndex + direction, true);
    };
    const onTouchEnd = (event) => finishTouch(event);
    const onTouchCancel = (event) => finishTouch(event, true);

    const onResize = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        if (disposed || !viewport.isConnected) return;
        if (viewport.clientWidth !== previousWidth) {
          previousWidth = viewport.clientWidth;
          stopMotion();
          stopIdle();
          const rebase = postCount + mod(targetIndex, postCount);
          targetIndex = rebase;
          latestIndex = mod(rebase, postCount);
          viewport.scrollLeft = centerOffset(rebase);
        }
        updateButtons();
      });
    };

    previous?.addEventListener('click', onPrevious);
    next?.addEventListener('click', onNext);
    viewport.addEventListener('keydown', onKeyDown);
    viewport.addEventListener('scroll', onScroll, { passive: true });
    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerCancel);
    viewport.addEventListener('dragstart', onDragStart);
    viewport.addEventListener('touchstart', onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    viewport.addEventListener('touchend', onTouchEnd, { passive: true });
    viewport.addEventListener('touchcancel', onTouchCancel, { passive: true });

    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(onResize) : null;
    observer?.observe(viewport);
    window.addEventListener('resize', onResize, { passive: true });

    window.requestAnimationFrame(() => {
      if (disposed) return;
      animateTo(postCount, false);
      updateButtons();
    });

    state.disposeNavigation = () => {
      disposed = true;
      observer?.disconnect();
      stopIdle();
      stopMotion();
      window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener('resize', onResize);
      previous?.removeEventListener('click', onPrevious);
      next?.removeEventListener('click', onNext);
      viewport.removeEventListener('keydown', onKeyDown);
      viewport.removeEventListener('scroll', onScroll);
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', onPointerUp);
      viewport.removeEventListener('pointercancel', onPointerCancel);
      viewport.removeEventListener('dragstart', onDragStart);
      viewport.removeEventListener('touchstart', onTouchStart);
      viewport.removeEventListener('touchmove', onTouchMove);
      viewport.removeEventListener('touchend', onTouchEnd);
      viewport.removeEventListener('touchcancel', onTouchCancel);
      touchGesture = null;
      drag = null;
      viewport.classList.remove('is-dragging');
    };
  };
  const initializeSection = (section) => {
    if (active.has(section)) return;
    const viewport = section.querySelector('[data-instagram-viewport]');
    const track = section.querySelector('[data-instagram-track]');
    const message = section.querySelector('[data-instagram-message]');
    if (!viewport || !track) return;

    const controller = new AbortController();
    const state = { controller, disposeNavigation: null };
    active.set(section, state);
    const designMode = section.dataset.instagramDesignMode === 'true';
    const feedUrl = (section.dataset.instagramFeedEndpoint || '').trim();
    const postLimit = Math.max(4, Math.min(12, Number(section.dataset.instagramPostLimit) || 12));

    if (!feedUrl) {
      track.replaceChildren();
      setMessage(message, designMode ? 'Enter the Cloudflare /feed URL in this section’s settings to display Instagram posts.' : 'Visit us on Instagram to see our latest posts.');
      return;
    }

    let endpoint;
    try {
      endpoint = new URL(feedUrl);
      if (endpoint.protocol !== 'https:') throw new Error('Invalid endpoint protocol');
      endpoint.searchParams.set('limit', String(postLimit));
    } catch {
      track.replaceChildren();
      setMessage(message, designMode ? 'The feed URL must be a full https:// URL.' : 'Instagram posts are temporarily unavailable.');
      return;
    }

    fetch(endpoint.toString(), { signal: controller.signal, mode: 'cors', headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error('Instagram feed unavailable');
        return response.json();
      })
      .then((payload) => {
        if (controller.signal.aborted || !section.isConnected) return;
        if (!payload || !Array.isArray(payload.posts)) throw new Error('Invalid feed format');
        const posts = payload.posts.filter((post) => post && validImage(post.imageUrl)).slice(0, postLimit);
        if (!posts.length) throw new Error('No posts available');

        const fragment = document.createDocumentFragment();
        for (let group = 0; group < 3; group += 1) {
          posts.forEach((post, index) => fragment.appendChild(createPost(post, index, group !== 1)));
        }
        track.replaceChildren(fragment);
        section.classList.add('twomatoes-instagram--ready');
        setMessage(message, `Showing ${posts.length} recent Instagram posts.`, true);
        setupCarousel(section, viewport, track, state, posts.length);
      })
      .catch((error) => {
        if (controller.signal.aborted || !section.isConnected) return;
        console.warn('[Twomatoes Instagram] Could not load the public feed.', error);
        track.replaceChildren();
        setMessage(message, designMode ? 'The Instagram feed could not be loaded. Check the Cloudflare URL and /feed response.' : 'Instagram posts are temporarily unavailable.');
      });
  };

  const initializeAll = (root = document) => {
    if (root.matches?.(SECTION_SELECTOR)) initializeSection(root);
    root.querySelectorAll?.(SECTION_SELECTOR).forEach(initializeSection);
  };

  const disposeAll = (root) => {
    const sections = [];
    if (root.matches?.(SECTION_SELECTOR)) sections.push(root);
    root.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => sections.push(section));
    sections.forEach((section) => {
      const state = active.get(section);
      if (!state) return;
      state.controller.abort();
      state.disposeNavigation?.();
      active.delete(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => disposeAll(event.target));
})();


/* Reviews v9: alternate photo and text-only testimonials in balanced columns.
   The original metaobject order stays in the markup as a no-JS fallback.
   Only the review list is rearranged; the featured review is untouched. */
(() => {
  const SELECTOR = '.twomatoes-webflow[data-twomatoes-section="reviews"] .block-testimonial-list';
  const active = new Map();

  const columnCount = () => {
    if (window.matchMedia('(max-width: 479px)').matches) return 1;
    if (window.matchMedia('(max-width: 991px)').matches) return 2;
    if (window.matchMedia('(max-width: 1200px)').matches) return 3;
    return 4;
  };

  const initialize = (list) => {
    if (active.has(list)) return;
    const cards = Array.from(list.children).filter((node) => node.classList.contains('block-testimonial-item'));
    if (!cards.length) return;

    const photoCards = cards.filter((card) => Boolean(card.querySelector('img.image-testimonial')));
    const textCards = cards.filter((card) => !card.querySelector('img.image-testimonial'));
    let previousCount = 0;
    let frame = 0;
    let destroyed = false;

    const render = () => {
      frame = 0;
      if (destroyed || !list.isConnected) return;
      const count = Math.min(columnCount(), cards.length);
      if (count === previousCount) return;
      previousCount = count;

      const columns = Array.from({ length: count }, () => {
        const column = document.createElement('div');
        column.className = 'twomatoes-reviews-zebra-column';
        return column;
      });
      // Copy arrays: preserve relative order among photo reviews and among text reviews.
      const photoQueue = photoCards.slice();
      const textQueue = textCards.slice();

      if (count === 1) {
        // Mobile: keep the original editorial/metaobject ordering.
        cards.forEach((card) => columns[0].appendChild(card));
      } else {
        for (let i = 0; i < cards.length; i += 1) {
          const col = i % count;
          const row = Math.floor(i / count);
          const preferPhoto = (row + col) % 2 === 0;
          const queue = preferPhoto ? (photoQueue.length ? photoQueue : textQueue) : (textQueue.length ? textQueue : photoQueue);
          const card = queue.shift();
          if (card) columns[col].appendChild(card);
        }
      }

      list.replaceChildren(...columns);
      list.classList.add('twomatoes-reviews-zebra-ready');
    };

    const schedule = () => {
      if (!frame && !destroyed) frame = window.requestAnimationFrame(render);
    };
    const resize = () => schedule();
    active.set(list, () => {
      destroyed = true;
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      list.replaceChildren(...cards);
      list.classList.remove('twomatoes-reviews-zebra-ready');
    });
    window.addEventListener('resize', resize, { passive: true });
    render();
  };

  const initializeAll = (root = document) => {
    if (root.matches?.(SELECTOR)) initialize(root);
    root.querySelectorAll?.(SELECTOR).forEach(initialize);
  };
  const disposeAll = (root) => {
    for (const [list, dispose] of active) {
      if (list === root || root.contains?.(list)) {
        dispose();
        active.delete(list);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => disposeAll(event.target));
})();


/* Twomatoes video reviews v2 — Metaobject gallery interactions, no separate asset. */
(() => {
  const SELECTOR = '.twomatoes-webflow.twomatoes-video-reviews';
  const active = new Map();
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const init = (section) => {
    if (active.has(section)) return;
    const viewport = section.querySelector('[data-vr-viewport]');
    if (!viewport) return;
    const cards = Array.from(viewport.querySelectorAll('[data-vr-card]'));
    if (!cards.length) return;
    const cleanups = [];
    const listen = (el, type, fn, options) => {
      el.addEventListener(type, fn, options);
      cleanups.push(() => el.removeEventListener(type, fn, options));
    };
    let raf = 0;
    let dragging = null;
    let touching = null;

    const total = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const offsetFor = (i) => Math.min(total(), Math.max(0, cards[i].offsetLeft - cards[0].offsetLeft));
    const nearest = () => {
      const pos = viewport.scrollLeft;
      let index = 0;
      let distance = Infinity;
      cards.forEach((card, i) => {
        const d = Math.abs(offsetFor(i) - pos);
        if (d < distance) { index = i; distance = d; }
      });
      return index;
    };
    const animateTo = (target, duration = 350) => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
      const from = viewport.scrollLeft;
      const delta = Math.max(0, Math.min(total(), target)) - from;
      if (Math.abs(delta) < 1 || prefersReducedMotion.matches) { viewport.scrollLeft = from + delta; return; }
      const start = performance.now();
      const frame = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        viewport.scrollLeft = from + delta * eased;
        if (t < 1) raf = window.requestAnimationFrame(frame);
        else raf = 0;
      };
      raf = window.requestAnimationFrame(frame);
    };
    const stopAnimation = () => { if (raf) window.cancelAnimationFrame(raf); raf = 0; };
    const snap = (gesture) => {
      if (!gesture || !gesture.horizontal || Math.abs(gesture.dx) < 8) {
        animateTo(offsetFor(gesture?.index ?? nearest()));
        return;
      }
      const width = cards[0].getBoundingClientRect().width;
      const elapsed = Math.max(1, performance.now() - gesture.startedAt);
      const fast = Math.abs(gesture.dx) / elapsed > .55;
      const steps = Math.abs(gesture.dx) > width * .16 || fast ? 1 : 0;
      const index = Math.max(0, Math.min(cards.length - 1, gesture.index + (gesture.dx < 0 ? steps : -steps)));
      animateTo(offsetFor(index), 360);
    };

    // One-card-per-gesture: touch events avoid Safari's inertial multi-card fling.
    const getTouch = (list, id) => Array.from(list).find((touch) => touch.identifier === id);
    listen(viewport, 'touchstart', (event) => {
      if (event.touches.length !== 1 || event.target.closest('button')) return;
      stopAnimation();
      const t = event.touches[0];
      touching = { id: t.identifier, x: t.clientX, y: t.clientY, scroll: viewport.scrollLeft, index: nearest(), dx: 0, horizontal: false, direction: null, startedAt: performance.now() };
    }, { passive: true });
    listen(viewport, 'touchmove', (event) => {
      if (!touching || event.touches.length !== 1) return;
      const t = getTouch(event.touches, touching.id);
      if (!t) return;
      const dx = t.clientX - touching.x;
      const dy = t.clientY - touching.y;
      if (!touching.direction && Math.max(Math.abs(dx), Math.abs(dy)) > 7) touching.direction = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      if (touching.direction !== 'horizontal') return;
      touching.horizontal = true;
      touching.dx = dx;
      if (event.cancelable) event.preventDefault();
      viewport.scrollLeft = touching.scroll - dx;
    }, { passive: false });
    listen(viewport, 'touchend', (event) => {
      if (!touching) return;
      if (getTouch(event.changedTouches, touching.id)) { const end = touching; touching = null; snap(end); }
    }, { passive: true });
    listen(viewport, 'touchcancel', () => { if (!touching) return; const end = touching; touching = null; animateTo(offsetFor(end.index)); }, { passive: true });

    // Desktop drag support, excluding play and sound buttons.
    listen(viewport, 'pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('button')) return;
      stopAnimation();
      dragging = { id: event.pointerId, x: event.clientX, scroll: viewport.scrollLeft, index: nearest(), dx: 0, horizontal: false, startedAt: performance.now() };
    });
    listen(viewport, 'pointermove', (event) => {
      if (!dragging || dragging.id !== event.pointerId) return;
      const dx = event.clientX - dragging.x;
      if (!dragging.horizontal && Math.abs(dx) < 6) return;
      dragging.horizontal = true;
      dragging.dx = dx;
      viewport.classList.add('is-dragging');
      try { viewport.setPointerCapture(event.pointerId); } catch (_) { /* optional */ }
      if (event.cancelable) event.preventDefault();
      viewport.scrollLeft = dragging.scroll - dx;
    });
    const release = (event) => {
      if (!dragging || dragging.id !== event.pointerId) return;
      const ended = dragging;
      dragging = null;
      viewport.classList.remove('is-dragging');
      try { if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId); } catch (_) { /* optional */ }
      snap(ended);
    };
    listen(viewport, 'pointerup', release);
    listen(viewport, 'pointercancel', release);
    listen(viewport, 'dragstart', (event) => event.preventDefault());
    listen(viewport, 'keydown', (event) => {
      if (event.target.closest('button')) return;
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      animateTo(offsetFor(Math.min(cards.length - 1, Math.max(0, nearest() + (event.key === 'ArrowRight' ? 1 : -1)))));
    });

    const videos = cards.map((card) => card.querySelector('video')).filter(Boolean);
    const update = (card, video) => {
      const media = card.querySelector('[data-vr-media]');
      const play = card.querySelector('[data-vr-play]');
      const sound = card.querySelector('[data-vr-sound]');
      const playing = !video.paused && !video.ended;
      media?.classList.toggle('is-playing', playing);
      media?.classList.toggle('is-unmuted', !video.muted);
      if (play) { play.setAttribute('aria-label', playing ? 'Pause video' : 'Play video'); play.setAttribute('aria-pressed', String(playing)); }
      if (sound) { sound.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off'); sound.setAttribute('aria-pressed', String(!video.muted)); }
    };
    cards.forEach((card) => {
      const video = card.querySelector('video');
      if (!video) return;
      const playButton = card.querySelector('[data-vr-play]');
      const soundButton = card.querySelector('[data-vr-sound]');
      video.muted = true;
      video.playsInline = true;
      const sync = () => update(card, video);
      ['play', 'pause', 'ended', 'volumechange'].forEach((type) => listen(video, type, sync));
      listen(playButton, 'click', async () => {
        if (!video.paused) { video.pause(); return; }
        document.querySelectorAll(`${SELECTOR} video`).forEach((other) => { if (other !== video) other.pause(); });
        try { await video.play(); } catch (_) { sync(); }
      });
      listen(soundButton, 'click', () => { video.muted = !video.muted; sync(); });
      sync();
    });

    // Pause sound/video when moved away, hidden, or scrolled out of view.
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (!entry.isIntersecting) entry.target.pause(); });
      }, { threshold: .3 });
      videos.forEach((video) => observer.observe(video));
    }
    const hidden = () => { if (document.hidden) videos.forEach((video) => video.pause()); };
    listen(document, 'visibilitychange', hidden);
    const resize = () => { stopAnimation(); viewport.scrollLeft = Math.min(viewport.scrollLeft, total()); };
    listen(window, 'resize', resize, { passive: true });
    active.set(section, () => { stopAnimation(); observer?.disconnect(); videos.forEach((video) => video.pause()); cleanups.forEach((fn) => fn()); });
  };
  const initializeAll = (root = document) => {
    if (root.matches?.(SELECTOR)) init(root);
    root.querySelectorAll?.(SELECTOR).forEach(init);
  };
  const disposeAll = (root) => {
    for (const [section, dispose] of active) {
      if (section === root || root.contains?.(section)) { dispose(); active.delete(section); }
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => disposeAll(event.target));
})();


/* Shared lightweight video lightbox for How It Works media blocks */
(() => {
  if (window.__twomatoesHowLightboxReady) return;
  window.__twomatoesHowLightboxReady = true;
  let dialog = null;
  let returnFocus = null;
  const create = () => {
    dialog = document.createElement('dialog');
    dialog.className = 'twomatoes-how-lightbox';
    dialog.setAttribute('aria-label', 'Video viewer');
    dialog.innerHTML = '<div class="twomatoes-how-lightbox__frame"><button class="twomatoes-how-lightbox__close" type="button" aria-label="Close video">×</button><video class="twomatoes-how-lightbox__video" controls playsinline preload="metadata"></video></div>';
    document.body.appendChild(dialog);
    dialog.querySelector('.twomatoes-how-lightbox__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => {
      const video = dialog.querySelector('video');
      video.pause();
      video.removeAttribute('src');
      video.removeAttribute('poster');
      video.load();
      returnFocus?.focus?.({ preventScroll: true });
      returnFocus = null;
    });
  };
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-twomatoes-lightbox-video]');
    if (!trigger) return;
    const src = trigger.dataset.videoSrc;
    if (!src || !/^https:\/\//.test(src)) return;
    event.preventDefault();
    if (!dialog) create();
    returnFocus = trigger;
    const video = dialog.querySelector('video');
    video.src = src;
    video.muted = false;
    if (trigger.dataset.videoPoster) video.poster = trigger.dataset.videoPoster;
    if (!dialog.open) dialog.showModal();
    video.play().catch(() => {});
  });
})();
